import type { GetCurrentMemberFn } from '@/features/iam'
import { FeatureToggles } from '@/features/feature-toggles'
import { action, computed, observable } from 'mobx'
import { env } from '@/infra/env'
import { type TxfJobStore } from '@/features/jobs/details/txf/TxfJobStore'
import { isNotNil } from '@/utils/object-util'
import { type Job } from '@/features/jobs/Job'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { JobDocumentsFn } from '@/features/documents'
import type { Document } from '@/features/documents/Document'
import { tapAction } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for the card that links a job to its TXF work layer job.
 */
export class TxfJobCardViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref private accessor _documents: Document[] = []

  constructor(
    private readonly _job: Job,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _txfJobStore: TxfJobStore,
    private readonly _jobDocuments: JobDocumentsFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(
      this._jobDocuments(this._job.id).pipe(
        tapAction((documents) => {
          this._documents = documents
        }),
      ),
    )
  }

  /**
   * Whether the current member may view the TXF job.
   */
  @computed
  get canViewTxfJob() {
    const currentMember = this._getCurrentMember(this._job.workspace.id)

    return (
      currentMember.permissions.adminViewTxfWorklayerJob ||
      this._job.championProvider?.member.user.id === currentMember.user.id
    )
  }

  /**
   * Whether the job has a TXF job.
   */
  @computed
  get hasIntegration() {
    return isNotNil(this._txfJobStore.forJob(this._job.id))
  }

  /**
   * Whether the job has unarchived documents.
   */
  @computed
  get hasDocuments() {
    return this._documents.some((document) => !document.archived)
  }

  /**
   * Whether the TXF job card is turned on.
   */
  @computed
  get isFeatureEnabled() {
    return FeatureToggles.txfJobCard()
  }

  /**
   * Whether the card is shown.
   */
  @computed
  get canView() {
    return (
      this.isFeatureEnabled &&
      this.hasDocuments &&
      this.hasIntegration &&
      this.canViewTxfJob
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this._fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Opens the TXF job in a new tab.
   */
  @action.bound
  viewInTxf() {
    window.open(
      `${env.TXF_PORTAL_URL}/job/${this._job.id}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  private async _fetch(signal: AbortSignal) {
    if (this.hasIntegration || !this.canViewTxfJob) {
      return
    }

    await this._createAlertRetryWrapper(
      'Loading the TXF job failed',
      signal,
    )(() => this._txfJobStore.fetchTxfJob(this._job.id, signal))
  }
}
